import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { demoRequestHref } from '@/content/site';
import {
  buildScheduleAnchorNotice,
  dayKeyInZone,
  formatDayInZone,
  formatMeetingConfirmation,
  formatSlotLabelForViewer,
} from '@/lib/demoSchedulingTime';
import { resolveVisitorContext } from '@/lib/visitorLocale';
import '@/components/ContactForm.css';
import './ReschedulePage.css';

type DemoSlot = {
  scheduledAt: string;
  label: string;
};

type PageData = {
  name: string;
  company: string;
  email: string;
  scheduledAt: string;
  meetingLabel: string;
  meetingTimezoneNote: string;
  meetUrl: string | null;
  slots: DemoSlot[];
};

type SuccessData = {
  scheduledAt: string;
  meetUrl: string;
  meetingLabel: string;
};

function apiBase(): string | null {
  if (import.meta.env.DEV) return '/api/public';
  const explicit = import.meta.env.VITE_CONTACT_ENDPOINT?.trim();
  if (explicit) {
    const u = explicit.replace(/\/demo-request\/?$/, '');
    return u || null;
  }
  const api = import.meta.env.VITE_API_ORIGIN?.trim();
  if (api) return `${api.replace(/\/$/, '')}/api/public`;
  return null;
}

function formatDayLabel(iso: string, timeZone: string): string {
  try {
    return formatDayInZone(iso, timeZone);
  } catch {
    return iso.slice(0, 10);
  }
}

export function ReschedulePage() {
  const { token = '' } = useParams<{ token: string }>();
  const visitor = useMemo(() => resolveVisitorContext(), []);
  const viewerTimeZone = visitor.scheduleTimeZone;
  const base = apiBase();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState<PageData | null>(null);
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [success, setSuccess] = useState<SuccessData | null>(null);
  const [cancelled, setCancelled] = useState(false);

  const scheduleAnchorNotice = useMemo(
    () => buildScheduleAnchorNotice(viewerTimeZone, visitor.countryIso),
    [viewerTimeZone, visitor.countryIso]
  );

  const load = useCallback(async () => {
    if (!base || !token) {
      setError('Link inválido.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const from = new Date().toISOString().slice(0, 10);
      const end = new Date();
      end.setUTCDate(end.getUTCDate() + 30);
      const to = end.toISOString().slice(0, 10);
      const res = await fetch(
        `${base}/demo-reschedule/${encodeURIComponent(token)}?from=${from}&to=${to}`,
        { headers: { Accept: 'application/json' } }
      );
      const json = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        data?: PageData;
        error?: { message?: string };
      };
      if (!res.ok || !json.success || !json.data) {
        throw new Error(json.error?.message || 'No pudimos cargar tu reunión.');
      }
      setPage(json.data);
      const slots = json.data.slots ?? [];
      if (slots[0]) {
        setSelectedDay(dayKeyInZone(slots[0].scheduledAt, viewerTimeZone));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No pudimos cargar tu reunión.');
      setPage(null);
    } finally {
      setLoading(false);
    }
  }, [base, token, viewerTimeZone]);

  useEffect(() => {
    void load();
  }, [load]);

  const slotsByDay = useMemo(() => {
    const map = new Map<string, DemoSlot[]>();
    for (const slot of page?.slots ?? []) {
      const key = dayKeyInZone(slot.scheduledAt, viewerTimeZone);
      const list = map.get(key) ?? [];
      list.push(slot);
      map.set(key, list);
    }
    return map;
  }, [page?.slots, viewerTimeZone]);

  const availableDays = useMemo(() => [...slotsByDay.keys()].sort(), [slotsByDay]);
  const daySlots = selectedDay ? (slotsByDay.get(selectedDay) ?? []) : [];

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!base || !token || !selectedSlot) {
      setError('Elige un horario para continuar.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${base}/demo-reschedule/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          scheduledAt: selectedSlot,
          viewerTimeZone,
        }),
      });
      const json = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        data?: SuccessData;
        error?: { message?: string };
      };
      if (!res.ok || !json.success || !json.data) {
        throw new Error(json.error?.message || 'No pudimos reagendar.');
      }
      setSuccess(json.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No pudimos reagendar.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCancel() {
    if (!base || !token) {
      setError('Link inválido.');
      return;
    }
    const ok = window.confirm(
      '¿Confirmas que no vas a asistir? Vamos a cancelar esta demo y liberar el horario.'
    );
    if (!ok) return;
    setCancelling(true);
    setError('');
    try {
      const res = await fetch(`${base}/demo-reschedule/${encodeURIComponent(token)}/cancel`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
      });
      const json = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        error?: { message?: string };
      };
      if (!res.ok || !json.success) {
        throw new Error(json.error?.message || 'No pudimos cancelar.');
      }
      setCancelled(true);
      setPage(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No pudimos cancelar.');
    } finally {
      setCancelling(false);
    }
  }

  return (
    <div className="reschedule-page">
      <div className="reschedule-page__shell">
        <header className="reschedule-page__header">
          <Link to="/" className="reschedule-page__brand">
            Interactive Talker
          </Link>
          <h1>Gestionar demo</h1>
          <p>Reagenda o cancela si no puedes asistir. Te confirmamos por email.</p>
        </header>

        {loading ? <p className="contact-form__hint">Cargando…</p> : null}

        {!loading && error && !page && !success && !cancelled ? (
          <div className="reschedule-page__card">
            <p className="contact-form__error" role="alert">
              {error}
            </p>
            <Link to="/" className="btn btn-secondary">
              Ir al sitio
            </Link>
          </div>
        ) : null}

        {cancelled ? (
          <div className="reschedule-page__card contact-form glass-card">
            <h2 className="contact-form__step-title">Demo cancelada</h2>
            <p className="contact-form__step-lead">
              Liberamos el horario. Si quieres otra demo, escríbenos por WhatsApp.
            </p>
            <a
              href={demoRequestHref}
              className="btn btn-primary contact-form__submit"
              target="_blank"
              rel="noopener noreferrer"
            >
              Pedir nueva demo
            </a>
          </div>
        ) : null}

        {success ? (
          <div className="reschedule-page__card contact-form glass-card">
            <h2 className="contact-form__step-title">¡Listo!</h2>
            <p className="contact-form__step-lead">
              Tu demo quedó reagendada para{' '}
              <strong>
                {formatMeetingConfirmation(success.scheduledAt, viewerTimeZone).when}
              </strong>
              .
            </p>
            {success.meetUrl ? (
              <a
                className="btn btn-primary contact-form__submit"
                href={success.meetUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Abrir Google Meet
              </a>
            ) : null}
            <p className="contact-form__hint" style={{ marginTop: '1rem' }}>
              También te mandamos el nuevo detalle por correo.
            </p>
          </div>
        ) : null}

        {!loading && page && !success && !cancelled ? (
          <form className="contact-form glass-card reschedule-page__card" onSubmit={handleSubmit}>
            <div className="contact-form__step-header">
              <h3 className="contact-form__step-title">Hola, {page.name}</h3>
              <p className="contact-form__step-lead">
                Ahora tienes agendado: <strong>{page.meetingLabel}</strong> (
                {page.meetingTimezoneNote}).
              </p>
              <p className="contact-form__step-lead">{scheduleAnchorNotice}</p>
            </div>

            {availableDays.length === 0 ? (
              <p className="contact-form__error" role="alert">
                No hay horarios disponibles en los próximos días. Escríbenos respondiendo el mail.
              </p>
            ) : (
              <>
                <div className="contact-form__days" role="tablist" aria-label="Días disponibles">
                  {availableDays.map((day) => {
                    const sample = slotsByDay.get(day)?.[0];
                    const label = sample ? formatDayLabel(sample.scheduledAt, viewerTimeZone) : day;
                    const active = day === selectedDay;
                    return (
                      <button
                        key={day}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        className={`contact-form__day-btn${active ? ' contact-form__day-btn--active' : ''}`}
                        onClick={() => {
                          setSelectedDay(day);
                          setSelectedSlot('');
                          setError('');
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>

                <div className="contact-form__slots" role="group" aria-label="Horarios">
                  {daySlots.map((slot) => {
                    const active = slot.scheduledAt === selectedSlot;
                    const slotLabel = formatSlotLabelForViewer(slot.scheduledAt, viewerTimeZone);
                    return (
                      <button
                        key={slot.scheduledAt}
                        type="button"
                        className={`contact-form__slot-btn${active ? ' contact-form__slot-btn--active' : ''}`}
                        onClick={() => {
                          setSelectedSlot(slot.scheduledAt);
                          setError('');
                        }}
                      >
                        <span className="contact-form__slot-time">{slotLabel.primary}</span>
                        <span className="contact-form__slot-sub">{slotLabel.secondary}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {error ? (
              <p className="contact-form__error" role="alert">
                {error}
              </p>
            ) : null}

            <button
              type="submit"
              className="btn btn-primary contact-form__submit"
              disabled={submitting || cancelling || !selectedSlot || availableDays.length === 0}
            >
              {submitting ? 'Guardando…' : 'Confirmar nuevo horario'}
            </button>

            <div className="reschedule-page__cancel">
              <p className="contact-form__hint">¿No puedes asistir?</p>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={submitting || cancelling}
                onClick={() => void handleCancel()}
              >
                {cancelling ? 'Cancelando…' : 'Cancelar esta demo'}
              </button>
            </div>
          </form>
        ) : null}
      </div>
    </div>
  );
}
